/**
 * Enrichment registry — maps entity types and investigation contexts
 * to enrichment sources that can provide additional intelligence.
 *
 * This is the routing table that makes enrichment contextual:
 * - Cyber investigation with IP addresses → Shodan + VirusTotal + DNS
 * - Maritime investigation with vessels → no enrichment (AIS data is primary)
 * - Infrastructure investigation → BGPView + Shodan
 *
 * Pure deterministic code — no LLM.
 */

export type EnrichmentSourceId =
  | 'shodan'
  | 'bgpview'
  | 'whois'
  | 'dns'
  | 'virustotal';

export interface EnrichmentMapping {
  sourceId: EnrichmentSourceId;
  /** Entity types this enrichment applies to */
  entityTypes: string[];
  /** Investigation contexts where this enrichment is relevant */
  contexts: string[];
  /** Whether this source requires an API key */
  requiresKey: boolean;
  /** Priority (lower = checked first). Used when budget is limited. */
  priority: number;
}

const ENRICHMENT_MAPPINGS: EnrichmentMapping[] = [
  {
    sourceId: 'shodan',
    entityTypes: ['ASSET', 'INDICATOR', 'ORGANIZATION'],
    contexts: ['cyber', 'infrastructure', 'ics_scada'],
    requiresKey: true,
    priority: 1,
  },
  {
    sourceId: 'bgpview',
    entityTypes: ['ASSET', 'ORGANIZATION'],
    contexts: ['cyber', 'infrastructure', 'outage'],
    requiresKey: false,
    priority: 2,
  },
  {
    sourceId: 'virustotal',
    entityTypes: ['INDICATOR', 'ASSET'],
    contexts: ['cyber', 'malware', 'threat'],
    requiresKey: true,
    priority: 3,
  },
  {
    sourceId: 'whois',
    entityTypes: ['ASSET', 'ORGANIZATION'],
    contexts: ['cyber', 'attribution', 'infrastructure'],
    requiresKey: false,
    priority: 4,
  },
  {
    sourceId: 'dns',
    entityTypes: ['ASSET', 'INDICATOR'],
    contexts: ['cyber', 'infrastructure', 'attribution'],
    requiresKey: false,
    priority: 5,
  },
];

/**
 * Get enrichment sources relevant for a given entity type and investigation context.
 * Returns sources sorted by priority (lowest number first).
 */
export function getRelevantEnrichments(
  entityTypes: string[],
  contexts: string[],
  availableKeys: Set<string>,
): EnrichmentMapping[] {
  return ENRICHMENT_MAPPINGS
    .filter(m => {
      // Must match at least one entity type
      const typeMatch = m.entityTypes.some(t => entityTypes.includes(t));
      if (!typeMatch) return false;
      // Must match at least one context
      const contextMatch = m.contexts.some(c => contexts.includes(c));
      if (!contextMatch) return false;
      // Must have API key if required
      if (m.requiresKey && !availableKeys.has(m.sourceId)) return false;
      return true;
    })
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Infer investigation contexts from scope contract keywords and subject type.
 * Used by the task-builder to determine which enrichments to activate.
 */
export function inferContexts(
  subjectType: string,
  keywords: string[],
): string[] {
  const contexts: string[] = [];
  const text = keywords.join(' ').toLowerCase();

  if (text.includes('cyber') || text.includes('hack') || text.includes('malware') ||
      text.includes('breach') || text.includes('vulnerability')) {
    contexts.push('cyber');
  }
  if (text.includes('infrastructure') || text.includes('scada') || text.includes('ics') ||
      text.includes('power') || text.includes('grid')) {
    contexts.push('infrastructure', 'ics_scada');
  }
  if (text.includes('outage') || text.includes('offline') || text.includes('bgp') ||
      text.includes('internet')) {
    contexts.push('outage', 'infrastructure');
  }
  if (text.includes('malware') || text.includes('ransomware') || text.includes('trojan')) {
    contexts.push('malware', 'threat');
  }
  if (text.includes('attribution') || text.includes('who') || text.includes('behind')) {
    contexts.push('attribution');
  }
  if (subjectType === 'INDICATOR' || subjectType === 'ASSET') {
    contexts.push('cyber');
  }

  return Array.from(new Set(contexts));
}
